import { useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, range } from 'lodash';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import { getRelayFaxDocumentLinksApi } from 'api/relay';

export const NO_FAX_DOCUMENTS_MESSAGE = 'No fax document has been sent for this order yet.';
export const FAX_DOCUMENTS_FAILED_MESSAGE = 'The fax document could not be downloaded. Try again.';

// The link is a short-lived signed URL to a PHI document: used once, never stored or logged.
const saveDocument = ({ downloadUrl, fileName }) => {
  const anchor = document.createElement('a');
  anchor.href = downloadUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};

/** POM-6279 — one button per fax PDF (browsers block several downloads from one click). */
const RelayFaxDocumentDownload = ({ relayOrderId, documentCount }) => {
  const [loadingNumber, setLoadingNumber] = useState(null);
  const [notice, setNotice] = useState(null);

  const handleDownload = async (documentNumber) => {
    setLoadingNumber(documentNumber);
    setNotice(null);

    try {
      const links = await getRelayFaxDocumentLinksApi(relayOrderId);

      const link = links.find((candidate) => candidate.documentNumber === documentNumber);

      if (isEmpty(links)) {
        setNotice({ severity: 'info', message: NO_FAX_DOCUMENTS_MESSAGE });
      } else if (link) {
        saveDocument(link);
      } else {
        setNotice({ severity: 'error', message: FAX_DOCUMENTS_FAILED_MESSAGE });
      }
    } catch {
      setNotice({ severity: 'error', message: FAX_DOCUMENTS_FAILED_MESSAGE });
    } finally {
      setLoadingNumber(null);
    }
  };

  const documentButton = (documentNumber, label) => (
    <Button
      key={label}
      variant='outlined'
      size='small'
      startIcon={
        loadingNumber === documentNumber ? <CircularProgress size={16} /> : <FileDownloadIcon />
      }
      disabled={loadingNumber !== null}
      onClick={() => handleDownload(documentNumber)}
    >
      {label}
    </Button>
  );

  return (
    <Box sx={{ pt: 2 }}>
      <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
        {documentCount > 1
          ? range(1, documentCount + 1).map((number) =>
              documentButton(number, `Download PDF ${number}`)
            )
          : documentButton(1, 'Download PDF')}
      </Stack>
      {notice && (
        <Alert severity={notice.severity} sx={{ mt: 1 }}>
          {notice.message}
        </Alert>
      )}
    </Box>
  );
};

RelayFaxDocumentDownload.propTypes = {
  relayOrderId: PropTypes.string.isRequired,
  documentCount: PropTypes.number,
};

RelayFaxDocumentDownload.defaultProps = { documentCount: 1 };

export default RelayFaxDocumentDownload;
